import axios from 'axios';

const ACCESS_TOKEN_STORAGE_KEY = 'hrms_access_token';
const API_TIMEOUT_STORAGE_KEY = 'hrms_api_timeout_ms';
const DEFAULT_API_TIMEOUT_MS = 20000;
const MIN_API_TIMEOUT_MS = 5000;
const MAX_API_TIMEOUT_MS = 300000;

const normalizeApiBaseUrl = (url) => {
  const trimmed = String(url || '').trim().replace(/\/$/, '');
  if (!trimmed) return '';
  return trimmed;
};

const shouldUseLocalDevProxy = () => String(process.env.NODE_ENV || '').toLowerCase() === 'development';

const inferLocalApiBaseUrl = () => {
  if (typeof window === 'undefined') return '';
  if (shouldUseLocalDevProxy()) return '';
  const protocol = window.location.protocol === 'https:' ? 'https:' : 'http:';
  const hostname = String(window.location.hostname || '').trim().toLowerCase();
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return `${protocol}//127.0.0.1:8000`;
  }
  return '';
};

const CONFIGURED_API_URL = normalizeApiBaseUrl(process.env.REACT_APP_BACKEND_URL || '');
let resolvedApiBaseUrl = inferLocalApiBaseUrl() || CONFIGURED_API_URL;
const NETWORK_RETRY_DELAY_MS = 350;

const getApiBaseUrl = () => resolvedApiBaseUrl || '';

const clampApiTimeoutMs = (value) => {
  const parsed = Number.parseInt(String(value || ''), 10);
  if (!Number.isFinite(parsed)) return DEFAULT_API_TIMEOUT_MS;
  return Math.min(MAX_API_TIMEOUT_MS, Math.max(MIN_API_TIMEOUT_MS, parsed));
};

const getStoredApiTimeoutMs = () => {
  if (typeof window === 'undefined') return DEFAULT_API_TIMEOUT_MS;
  return clampApiTimeoutMs(window.localStorage.getItem(API_TIMEOUT_STORAGE_KEY));
};

let resolvedApiTimeoutMs = getStoredApiTimeoutMs();

export const setApiTimeoutMs = (value) => {
  resolvedApiTimeoutMs = clampApiTimeoutMs(value);
  api.defaults.timeout = resolvedApiTimeoutMs;
  if (typeof window !== 'undefined') {
    window.localStorage.setItem(API_TIMEOUT_STORAGE_KEY, String(resolvedApiTimeoutMs));
  }
  return resolvedApiTimeoutMs;
};

const syncApiTimeoutFromSettings = (settingsPayload) => {
  const timeoutSeconds = Number.parseInt(String(settingsPayload?.api_timeout_seconds || ''), 10);
  if (!Number.isFinite(timeoutSeconds) || timeoutSeconds <= 0) return;
  setApiTimeoutMs(timeoutSeconds * 1000);
};

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
const isNetworkLayerError = (error) => {
  const message = String(error?.message || '').toLowerCase();
  const code = String(error?.code || '');
  return (
    !error?.response &&
    (code === 'ERR_NETWORK' || code === 'ECONNABORTED' || message.includes('network error') || message.includes('timeout'))
  );
};

const getFallbackBaseUrls = (currentBaseUrl = '') => {
  const current = normalizeApiBaseUrl(currentBaseUrl);
  const configured = CONFIGURED_API_URL;

  let local8000 = '';
  let localhost8000 = '';
  if (typeof window !== 'undefined') {
    const protocol = window.location.protocol === 'https:' ? 'https:' : 'http:';
    local8000 = `${protocol}//127.0.0.1:8000`;
    localhost8000 = `${protocol}//localhost:8000`;
  }

  // Only a development build guesses. In production the API lives at one
  // address and the SPA at another, so the last three of these are wrong by
  // construction: a browser on the public internet has no :8000, and the SPA's
  // own origin serves static files - `POST /api/auth/login` there is answered
  // 405 by the file server. A CORS rejection or any other network-layer failure
  // would retry into that 405 and report it, which is how a refused origin came
  // back looking like a broken login route.
  const candidates = shouldUseLocalDevProxy()
    ? ['', current, configured, localhost8000, local8000]
    : [current, configured];

  const seen = new Set();
  const unique = [];
  for (const value of candidates) {
    const normalized = value === '' ? '' : normalizeApiBaseUrl(value);
    if (seen.has(normalized)) continue;
    seen.add(normalized);
    unique.push(normalized);
  }
  return unique;
};

const inferDisplayedBackendTarget = (requestBaseUrl = '') => {
  const explicitBase = String(requestBaseUrl || '').trim() || getApiBaseUrl();
  if (explicitBase) return explicitBase;
  if (shouldUseLocalDevProxy() && typeof window !== 'undefined') {
    const protocol = window.location.protocol === 'https:' ? 'https:' : 'http:';
    const hostname = String(window.location.hostname || '').trim().toLowerCase() || 'localhost';
    return `${protocol}//${hostname}:8000`;
  }
  return typeof window !== 'undefined' ? window.location.origin : 'configured backend';
};

const enrichNetworkError = (error) => {
  if (!error) return error;
  const requestBaseUrl = String(error?.config?.baseURL || '').trim();
  const backendTarget = inferDisplayedBackendTarget(requestBaseUrl);
  const timedOut = String(error?.code || '') === 'ECONNABORTED' || String(error?.message || '').toLowerCase().includes('timeout');
  const detail = timedOut
    ? `Request timed out while contacting backend (${backendTarget}).`
    : `Unable to reach backend (${backendTarget}). Please verify backend is running and reachable.`;
  error.message = detail;
  if (!error.response) {
    error.response = { data: { detail } };
  } else if (!error.response.data) {
    error.response.data = { detail };
  } else if (!error.response.data.detail) {
    error.response.data.detail = detail;
  }
  return error;
};

const enrichAuthError = (error) => {
  if (!error) return error;
  const detail = 'Session expired or invalid credentials. Please sign in again.';
  error.message = detail;
  if (!error.response) {
    error.response = { status: 401, data: { detail } };
  } else if (!error.response.data) {
    error.response.data = { detail };
  } else {
    error.response.data.detail = detail;
  }
  return error;
};

export const buildApiUrl = (path) => {
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  const baseUrl = getApiBaseUrl();
  return baseUrl ? `${baseUrl}${normalizedPath}` : normalizedPath;
};

export const buildAssetUrl = (path) => {
  if (!path) return '';
  if (/^https?:\/\//i.test(path)) return path;

  const normalizedPath = path.startsWith('/') ? path : `/${path}`;

  const baseUrl = getApiBaseUrl();
  if (baseUrl) {
    return `${baseUrl}${normalizedPath}`;
  }

  if (typeof window !== 'undefined') {
    return `${window.location.origin}${normalizedPath}`;
  }

  return normalizedPath;
};

export const api = axios.create({
  baseURL: getApiBaseUrl(),
  withCredentials: true,
  timeout: resolvedApiTimeoutMs,
  headers: {
    'Content-Type': 'application/json',
  },
});

const rememberApiBaseUrl = (baseUrl) => {
  const normalizedBaseUrl = normalizeApiBaseUrl(baseUrl);
  if (!normalizedBaseUrl) return;
  resolvedApiBaseUrl = normalizedBaseUrl;
  api.defaults.baseURL = normalizedBaseUrl;
};

const isBrowser = typeof window !== 'undefined';

export const getStoredAccessToken = () => {
  if (!isBrowser) return null;
  return window.localStorage.getItem(ACCESS_TOKEN_STORAGE_KEY);
};

export const setAccessToken = (token) => {
  if (!token) return;
  api.defaults.headers.common.Authorization = `Bearer ${token}`;
  if (isBrowser) {
    window.localStorage.setItem(ACCESS_TOKEN_STORAGE_KEY, token);
  }
};

export const clearAccessToken = () => {
  delete api.defaults.headers.common.Authorization;
  if (isBrowser) {
    window.localStorage.removeItem(ACCESS_TOKEN_STORAGE_KEY);
  }
};

const bootstrapToken = getStoredAccessToken();
if (bootstrapToken) {
  api.defaults.headers.common.Authorization = `Bearer ${bootstrapToken}`;
}

export const authAPI = {
  login: (email, password) => api.post('/api/auth/login', { email, password }),
  verifyMfa: (challengeToken, code) => api.post('/api/auth/mfa/verify', { challenge_token: challengeToken, code }),
  requestLoginOtp: (identifier) => api.post('/api/auth/otp/request', { identifier }),
  verifyLoginOtp: (identifier, code) => api.post('/api/auth/otp/verify', { identifier, code }),
  logout: () => api.post('/api/auth/logout'),
  me: () => api.get('/api/auth/me'),
  refresh: () => api.post('/api/auth/refresh'),
  register: (email, password, role, options = {}) =>
    api.post('/api/auth/register', { email, password, role, ...options }),
  requestPasswordReset: (email) => api.post('/api/auth/request-password-reset', { email }),
  resetPassword: (token, newPassword) => api.post('/api/auth/reset-password', { token, new_password: newPassword }),
  requestEmailVerification: (email) => api.post('/api/auth/request-email-verification', { email }),
  verifyEmail: (token) => api.post('/api/auth/verify-email', { token }),
};

export const marketplaceAPI = {
  listApps: (params) => api.get('/api/marketplace/apps', { params }),
  appDetail: (appId) => api.get(`/api/marketplace/apps/${appId}`),
  installed: () => api.get('/api/marketplace/installed'),
  install: (appId) => api.post(`/api/marketplace/apps/${appId}/install`),
  uninstall: (appId) => api.delete(`/api/marketplace/apps/${appId}/install`),
};

export const verticalAPI = {
  // Resolved bundle for the current tenant (terminology, navigation, dashboards, roles).
  getConfig: () => api.get('/api/vertical/config'),
  // All available verticals — drives the Super Admin dropdown dynamically.
  listRegistry: () => api.get('/api/vertical/registry'),
  // Superadmin preview of a vertical's resolved bundle.
  previewRegistry: (key) => api.get(`/api/vertical/registry/${key}`),
  // The authoring surface: list, read, save, retire.
  listDefinitions: () => api.get('/api/vertical/definitions'),
  getDefinition: (key) => api.get(`/api/vertical/definitions/${key}`),
  saveDefinition: (key, body) => api.put(`/api/vertical/definitions/${key}`, body),
};

/** The add-on catalog. One source, read by the console and by the grant path
 *  in plan_access - which is what stops the two drifting the way they did. */
export const addonAPI = {
  registry: () => api.get('/api/addons/registry'),
  // Resolved for one vertical: its exclusions applied, its label and price
  // overrides merged, and what it does NOT get with the reason why.
  forVertical: (key) => api.get(`/api/addons/for-vertical/${key}`),
  validate: (key) => api.get(`/api/addons/for-vertical/${key}/validate`),
  // Author one without a deploy. Layered over addons/catalog.json the same way
  // vertical_definitions layers over verticals/*.json.
  save: (key, body) => api.put(`/api/addons/definitions/${key}`, body),
  remove: (key) => api.delete(`/api/addons/definitions/${key}`),
};

export const workflowAPI = {
  listTemplates: () => api.get('/api/workflows/templates'),
  getTemplate: (key) => api.get(`/api/workflows/templates/${key}`),
  listInstances: (params) => api.get('/api/workflows/instances', { params }),
  getInstance: (id) => api.get(`/api/workflows/instances/${id}`),
  start: (data) => api.post('/api/workflows/instances', data),
  act: (id, data) => api.post(`/api/workflows/instances/${id}/action`, data),
  comment: (id, data) => api.post(`/api/workflows/instances/${id}/comment`, data),
  cancel: (id, data) => api.post(`/api/workflows/instances/${id}/cancel`, data),
  runSla: (id) => api.post(`/api/workflows/instances/${id}/run-sla`, {}),
  // Workflow Builder (DB-authored templates — no code changes needed)
  listBuilderTemplates: () => api.get('/api/workflows/builder/templates'),
  saveBuilderTemplate: (data) => api.post('/api/workflows/builder/templates', data),
  deleteBuilderTemplate: (key) => api.delete(`/api/workflows/builder/templates/${key}`),
  // Delegate / proxy approvers
  listDelegations: () => api.get('/api/workflows/delegations'),
  createDelegation: (data) => api.post('/api/workflows/delegations', data),
  revokeDelegation: (id) => api.delete(`/api/workflows/delegations/${id}`),
  // In-app notification inbox
  listNotifications: (params) => api.get('/api/workflows/notifications', { params }),
  markNotificationRead: (id) => api.post(`/api/workflows/notifications/${id}/read`, {}),
};

export const systemAPI = {
  getFeatureHealth: () => api.get('/api/health/features'),
};

let refreshPromise = null;

const DEVICE_ID_STORAGE_KEY = 'vitehr.device_id';

/**
 * A stable id for this browser profile, for the single-device policy.
 *
 * Deliberately a random uuid in localStorage rather than a fingerprinting
 * library: fingerprinting is a privacy liability, it breaks under ordinary
 * browser hardening, and it is no more honest than this — either can be
 * cleared, so the policy is a control on honest use, not a guarantee.
 */
export const getDeviceId = () => {
  if (!isBrowser) return null;
  let id = window.localStorage.getItem(DEVICE_ID_STORAGE_KEY);
  if (!id) {
    id = (window.crypto?.randomUUID?.() || `web-${Date.now()}-${Math.random().toString(16).slice(2)}`);
    window.localStorage.setItem(DEVICE_ID_STORAGE_KEY, id);
  }
  return id;
};

api.interceptors.request.use(
  (config) => {
    // Sent on every request rather than only at login so the server can
    // recognise this browser wherever it is useful. Ignored where it is not.
    const deviceId = getDeviceId();
    if (deviceId) {
      config.headers = config.headers || {};
      config.headers['X-Device-Id'] = deviceId;
      config.headers['X-Device-Kind'] = 'desktop';
      config.headers['X-Client'] = 'web';
    }
    return config;
  },
  (error) => Promise.reject(error)
);

api.interceptors.response.use(
  (response) => {
    rememberApiBaseUrl(response?.config?.baseURL);
    syncApiTimeoutFromSettings(response?.data);
    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    const status = error.response?.status;
    const requestUrl = originalRequest?.url || '';
    const isAuthRefreshRequest = requestUrl.includes('/api/auth/refresh');
    const isAuthBootstrapRequest =
      requestUrl.includes('/api/auth/login') ||
      requestUrl.includes('/api/auth/logout') ||
      requestUrl.includes('/api/auth/mfa/verify') ||
      // A rejected sign-in code is a 401 by design; refreshing on it would
      // bounce an unauthenticated visitor through the refresh path for nothing.
      requestUrl.includes('/api/auth/otp/');

    if (isNetworkLayerError(error) && originalRequest) {
      if (!originalRequest._networkRetry) {
        originalRequest._networkRetry = true;
        await wait(NETWORK_RETRY_DELAY_MS);
        const fallbackBaseUrls = getFallbackBaseUrls(originalRequest?.baseURL || api.defaults.baseURL || '');
        for (const baseUrl of fallbackBaseUrls) {
          try {
            return await api({ ...originalRequest, baseURL: baseUrl });
          } catch (retryError) {
            if (!isNetworkLayerError(retryError)) {
              return Promise.reject(retryError);
            }
          }
        }
        return Promise.reject(enrichNetworkError(error));
      }
    }

    if (
      status !== 401 ||
      !originalRequest ||
      originalRequest._retry ||
      isAuthRefreshRequest ||
      isAuthBootstrapRequest
    ) {
      return Promise.reject(error);
    }

    originalRequest._retry = true;

    try {
      refreshPromise =
        refreshPromise ||
        authAPI
          .refresh()
          .then((response) => {
            const refreshedToken = response?.data?.access_token;
            if (refreshedToken) {
              setAccessToken(refreshedToken);
            }
            return response;
          })
          .finally(() => {
            refreshPromise = null;
          });
      const refreshResponse = await refreshPromise;
      const refreshedToken = refreshResponse?.data?.access_token;
      if (refreshedToken) {
        originalRequest.headers = {
          ...(originalRequest.headers || {}),
          Authorization: `Bearer ${refreshedToken}`,
        };
      }
      return api(originalRequest);
    } catch (refreshError) {
      if (refreshError?.response?.status === 401) {
        clearAccessToken();
        return Promise.reject(enrichAuthError(refreshError));
      }
      return Promise.reject(isNetworkLayerError(refreshError) ? enrichNetworkError(refreshError) : refreshError);
    }
  }
);

export const hrLifecycleAPI = {
  list: (params) => api.get('/api/hr-lifecycle/events', { params }),
  get: (id) => api.get(`/api/hr-lifecycle/events/${id}`),
  create: (data) => api.post('/api/hr-lifecycle/events', data),
  update: (id, data) => api.put(`/api/hr-lifecycle/events/${id}`, data),
  approve: (id, comments) => api.post(`/api/hr-lifecycle/events/${id}/approve`, { comments }),
  reject: (id, comments) => api.post(`/api/hr-lifecycle/events/${id}/reject`, { comments }),
  remove: (id) => api.delete(`/api/hr-lifecycle/events/${id}`),
  summary: () => api.get('/api/hr-lifecycle/summary'),
  history: (employeeId) => api.get(`/api/hr-lifecycle/employees/${employeeId}/history`),
};

// Recruitment / ATS (backend-first module). convertToEmployee provisions a hired
// candidate as a real employee via the canonical creation path.
export const recruitmentAPI = {
  listJobs: (params) => api.get('/api/recruitment/jobs', { params }),
  createJob: (data) => api.post('/api/recruitment/jobs', data),
  updateJob: (id, data) => api.put(`/api/recruitment/jobs/${id}`, data),
  listApplications: (params) => api.get('/api/recruitment/applications', { params }),
  listCandidates: (params) => api.get('/api/recruitment/candidates', { params }),
  getStages: () => api.get('/api/recruitment/stages'),
  moveStage: (applicationId, data) => api.post(`/api/recruitment/applications/${applicationId}/stage`, data),
  convertToEmployee: (applicationId, data) =>
    api.post(`/api/recruitment/applications/${applicationId}/convert-to-employee`, data || {}),
  analytics: () => api.get('/api/recruitment/analytics/overview'),
};

// Tenant-defined leave type catalog (built-ins + custom types).
export const leaveTypesAPI = {
  list: (params) => api.get('/api/leave-types', { params }),
  get: (id) => api.get(`/api/leave-types/${id}`),
  create: (data) => api.post('/api/leave-types', data),
  update: (id, data) => api.put(`/api/leave-types/${id}`, data),
  remove: (id) => api.delete(`/api/leave-types/${id}`),
};

// First-class Attendance Regularization (delegates to the work-requests engine).
export const attendanceRegularizationAPI = {
  apply: (data) => api.post('/api/attendance/regularization/apply', data),
  my: (params) => api.get('/api/attendance/regularization/my', { params }),
  team: (params) => api.get('/api/attendance/regularization/team', { params }),
  approve: (id, data) => api.post(`/api/attendance/regularization/${id}/approve`, data || {}),
  reject: (id, data) => api.post(`/api/attendance/regularization/${id}/reject`, data),
  cancel: (id, reason) => api.post(`/api/attendance/regularization/${id}/cancel`, { reason }),
  edit: (id, data) => api.put(`/api/attendance/regularization/${id}/edit`, data),
};

// Unified per-employee lifecycle timeline (aggregates lifecycle, exit, contracts, recognition).
export const employeeTimelineAPI = {
  get: (employeeId, params) => api.get(`/api/employee-timeline/${employeeId}`, { params }),
  summary: (employeeId) => api.get(`/api/employee-timeline/${employeeId}/summary`),
};

// HR Action Center — everything awaiting an HR decision across lifecycle modules.
export const actionCenterAPI = {
  pending: (params) => api.get('/api/action-center/pending', { params }),
  summary: (params) => api.get('/api/action-center/summary', { params }),
};

// Generic Enterprise Report Engine — single API for every registered report.
export const reportEngineAPI = {
  catalog: () => api.get('/api/reports/engine/catalog'),
  metadata: (reportId) => api.get(`/api/reports/engine/reports/${reportId}`),
  options: (reportId, source) =>
    api.get(`/api/reports/engine/reports/${reportId}/options`, { params: { source } }),
  run: (reportId, params) => api.get(`/api/reports/engine/reports/${reportId}/run`, { params }),
  export: (reportId, params, format = 'csv') =>
    api.get(`/api/reports/engine/reports/${reportId}/export`, {
      params: { format, ...params },
      responseType: 'blob',
    }),
  listSavedViews: (reportId) =>
    api.get('/api/reports/engine/saved-views', { params: reportId ? { report_id: reportId } : {} }),
  createSavedView: (data) => api.post('/api/reports/engine/saved-views', data),
  updateSavedView: (id, data) => api.put(`/api/reports/engine/saved-views/${id}`, data),
  deleteSavedView: (id) => api.delete(`/api/reports/engine/saved-views/${id}`),
  listPinnedViews: () => api.get('/api/reports/engine/saved-views-pinned'),
  // Async Report Jobs
  createJob: (data) => api.post('/api/reports/engine/jobs', data),
  listJobs: (params) => api.get('/api/reports/engine/jobs', { params }),
  getJob: (id) => api.get(`/api/reports/engine/jobs/${id}`),
  downloadJob: (id) => api.get(`/api/reports/engine/jobs/${id}/download`, { responseType: 'blob' }),
  cancelJob: (id) => api.post(`/api/reports/engine/jobs/${id}/cancel`),
  retryJob: (id) => api.post(`/api/reports/engine/jobs/${id}/retry`),
  // Scheduled Reports
  listSchedules: (reportId) =>
    api.get('/api/reports/engine/schedules', { params: reportId ? { report_id: reportId } : {} }),
  createSchedule: (data) => api.post('/api/reports/engine/schedules', data),
  updateSchedule: (id, data) => api.put(`/api/reports/engine/schedules/${id}`, data),
  deleteSchedule: (id) => api.delete(`/api/reports/engine/schedules/${id}`),
  runScheduleNow: (id) => api.post(`/api/reports/engine/schedules/${id}/run-now`),
};

// Exit Management / Offboarding
export const exitManagementAPI = {
  list: (params) => api.get('/api/exit-management/exits', { params }),
  get: (id) => api.get(`/api/exit-management/exits/${id}`),
  create: (data) => api.post('/api/exit-management/exits', data),
  update: (id, data) => api.put(`/api/exit-management/exits/${id}`, data),
  approve: (id, comments) => api.post(`/api/exit-management/exits/${id}/approve`, { comments }),
  reject: (id, comments) => api.post(`/api/exit-management/exits/${id}/reject`, { comments }),
  clearance: (id, area, cleared, notes) =>
    api.post(`/api/exit-management/exits/${id}/clearance`, { area, cleared, notes }),
  complete: (id, comments) => api.post(`/api/exit-management/exits/${id}/complete`, { comments }),
  remove: (id) => api.delete(`/api/exit-management/exits/${id}`),
  history: (employeeId) => api.get(`/api/exit-management/employees/${employeeId}/history`),
  summary: () => api.get('/api/exit-management/summary'),
};

// Document Templates
export const docTemplatesAPI = {
  list: (params) => api.get('/api/doc-templates/templates', { params }),
  get: (id) => api.get(`/api/doc-templates/templates/${id}`),
  create: (data) => api.post('/api/doc-templates/templates', data),
  update: (id, data) => api.put(`/api/doc-templates/templates/${id}`, data),
  publish: (id) => api.post(`/api/doc-templates/templates/${id}/publish`),
  archive: (id) => api.post(`/api/doc-templates/templates/${id}/archive`),
  remove: (id) => api.delete(`/api/doc-templates/templates/${id}`),
  versions: (id) => api.get(`/api/doc-templates/templates/${id}/versions`),
  preview: (id, data) => api.post(`/api/doc-templates/templates/${id}/preview`, data),
  generate: (id, data) => api.post(`/api/doc-templates/templates/${id}/generate`, data),
  listGenerated: (params) => api.get('/api/doc-templates/generated', { params }),
  getGenerated: (id) => api.get(`/api/doc-templates/generated/${id}`),
  summary: () => api.get('/api/doc-templates/summary'),
};

// AI Assistant (multi-provider chat)
export const aiAssistantAPI = {
  getConfig: () => api.get('/api/ai-assistant/config'),
  getProviders: () => api.get('/api/ai-assistant/providers'),
  getSettings: () => api.get('/api/ai-assistant/settings'),
  updateSettings: (data) => api.put('/api/ai-assistant/settings', data),
  testProvider: () => api.post('/api/ai-assistant/settings/test'),
  getUsage: () => api.get('/api/ai-assistant/usage'),
  listConversations: (params) => api.get('/api/ai-assistant/conversations', { params }),
  createConversation: (data) => api.post('/api/ai-assistant/conversations', data || {}),
  updateConversation: (id, data) => api.patch(`/api/ai-assistant/conversations/${id}`, data),
  deleteConversation: (id) => api.delete(`/api/ai-assistant/conversations/${id}`),
  getMessages: (id) => api.get(`/api/ai-assistant/conversations/${id}/messages`),
  chat: (data) => api.post('/api/ai-assistant/chat', data),
  feedback: (messageId, value) => api.post(`/api/ai-assistant/messages/${messageId}/feedback`, { value }),
  // Multi-agent management
  listAgents: (params) => api.get('/api/ai-assistant/agents', { params }),
  getAgent: (id) => api.get(`/api/ai-assistant/agents/${id}`),
  createAgent: (data) => api.post('/api/ai-assistant/agents', data),
  updateAgent: (id, data) => api.put(`/api/ai-assistant/agents/${id}`, data),
  cloneAgent: (id) => api.post(`/api/ai-assistant/agents/${id}/clone`),
  setAgentStatus: (id, status) => api.post(`/api/ai-assistant/agents/${id}/status`, { status }),
  deleteAgent: (id) => api.delete(`/api/ai-assistant/agents/${id}`),
  // Usage analytics
  getAnalytics: () => api.get('/api/ai-assistant/analytics'),
};

// Employment Contracts
export const contractsAPI = {
  list: (params) => api.get('/api/contracts/contracts', { params }),
  get: (id) => api.get(`/api/contracts/contracts/${id}`),
  create: (data) => api.post('/api/contracts/contracts', data),
  update: (id, data) => api.put(`/api/contracts/contracts/${id}`, data),
  activate: (id) => api.post(`/api/contracts/contracts/${id}/activate`),
  sign: (id, party) => api.post(`/api/contracts/contracts/${id}/sign`, { party }),
  renew: (id, data) => api.post(`/api/contracts/contracts/${id}/renew`, data),
  terminate: (id, reason) => api.post(`/api/contracts/contracts/${id}/terminate`, { reason }),
  remove: (id) => api.delete(`/api/contracts/contracts/${id}`),
  expiring: (withinDays = 30) => api.get('/api/contracts/expiring', { params: { within_days: withinDays } }),
  runMaintenance: () => api.post('/api/contracts/maintenance/run'),
  history: (employeeId) => api.get(`/api/contracts/employees/${employeeId}/history`),
  summary: () => api.get('/api/contracts/summary'),
};

export const employeeAPI = {
  create: (data) => api.post('/api/employees/create', data),
  list: () => api.get('/api/employees'),
  get: (employeeId) => api.get(`/api/employees/${employeeId}`),
  update: (employeeId, data) => api.put(`/api/employees/${employeeId}`, data),
  delete: (employeeId) => api.delete(`/api/employees/${employeeId}`),
  previewSyncDepartmentMapping: () => api.get('/api/employees/sync-department-mapping/preview'),
  syncDepartmentMapping: () => api.post('/api/employees/sync-department-mapping'),
  updateHierarchy: (employeeId, data) => api.put(`/api/employees/${employeeId}/reporting-hierarchy`, data),
  getDirectReports: (employeeId) => api.get(`/api/employees/${employeeId}/direct-reports`),
  getLeaveBalance: (employeeId) => api.get(`/api/employees/${employeeId}/leave-balance`),
  getCarryForward: (employeeId) => api.get(`/api/employees/${employeeId}/leave-carry-forward`),
  assignLeavePolicy: (employeeId, policyId) => api.post(`/api/employees/${employeeId}/assign-leave-policy`, { policy_id: policyId }),
  
  // New Profile APIs
  getMyProfile: () => api.get('/api/employees/me'),
  updateMyProfile: (data) => api.put('/api/employees/me', data),
  uploadProfilePhoto: (formData) => api.post('/api/employees/me/photo', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  uploadEmployeePhoto: (employeeId, formData) => api.post(`/api/employees/${employeeId}/photo`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  getMyTeam: () => api.get('/api/employees/my-team'),
  getCoworkersOffThisWeek: () => api.get('/api/employees/my-team/off-this-week'),
  getMyHierarchy: () => api.get('/api/employees/my-hierarchy'),
  
  // Emergency Contacts
  getEmergencyContacts: () => api.get('/api/employees/me/emergency-contacts'),
  addEmergencyContact: (data) => api.post('/api/employees/me/emergency-contacts', data),
  updateEmergencyContact: (contactId, data) => api.put(`/api/employees/me/emergency-contacts/${contactId}`, data),
  deleteEmergencyContact: (contactId) => api.delete(`/api/employees/me/emergency-contacts/${contactId}`),
  
  // Admin Emergency Contacts
  getEmployeeEmergencyContacts: (employeeId) => api.get(`/api/employees/${employeeId}/emergency-contacts`),
  addEmployeeEmergencyContact: (employeeId, data) => api.post(`/api/employees/${employeeId}/emergency-contacts`, data),
  updateEmployeeEmergencyContact: (employeeId, contactId, data) => api.put(`/api/employees/${employeeId}/emergency-contacts/${contactId}`, data),
  deleteEmployeeEmergencyContact: (employeeId, contactId) => api.delete(`/api/employees/${employeeId}/emergency-contacts/${contactId}`),

  // Job History
  getJobHistory: () => api.get('/api/employees/me/job-history'),
  addJobHistory: (data) => api.post('/api/employees/me/job-history', data),
  updateJobHistory: (historyId, data) => api.put(`/api/employees/me/job-history/${historyId}`, data),
  
  // Admin Job History
  getEmployeeJobHistory: (employeeId) => api.get(`/api/employees/${employeeId}/job-history`),
  addEmployeeJobHistory: (employeeId, data) => api.post(`/api/employees/${employeeId}/job-history`, data),
  updateEmployeeJobHistory: (employeeId, historyId, data) => api.put(`/api/employees/${employeeId}/job-history/${historyId}`, data),
  deleteEmployeeJobHistory: (employeeId, historyId) => api.delete(`/api/employees/${employeeId}/job-history/${historyId}`),
  
  // Documents
  getMyDocuments: () => api.get('/api/employees/me/documents'),
  // Profile Change Requests (approval workflow)
  createProfileChangeRequest: (data) => api.post('/api/employees/me/profile-change-requests', data),
  getMyProfileChangeRequests: () => api.get('/api/employees/me/profile-change-requests'),
  listProfileChangeRequests: (params) => api.get('/api/employees/profile-change-requests', { params }),
  reviewProfileChangeRequest: (requestId, data) => api.put(`/api/employees/profile-change-requests/${requestId}/review`, data),
};

export const biometricAPI = {
  enroll: (data) => api.post('/api/biometric/enroll', data),
};

export const attendanceAPI = {
  mark: (data) => api.post('/api/attendance/mark', data),
  getMyRecords: () => api.get('/api/attendance/my-records'),
  // Every attempt, refusals included. `getMyRecords` is the register -
  // a punch the server turned away is not in it, so it cannot explain a
  // missing day.
  getMyAttempts: (params) => api.get('/api/attendance/my-attempts', { params }),
  getAllRecords: () => api.get('/api/attendance/all'),
  manualEntry: (data) => api.post('/api/attendance/manual-entry', null, { params: data }),
};

export const fraudAPI = {
  getAlerts: () => api.get('/api/fraud/alerts'),
};

export const geofenceAPI = {
  create: (data) => api.post('/api/geofence/create', null, { params: data }),
  list: () => api.get('/api/geofence/list'),
  delete: (geofenceId) => api.delete(`/api/geofence/${geofenceId}`),
};

// Leave Management APIs
export const leaveAPI = {
  // Leave requests
  apply: (data) => api.post('/api/leaves/apply', data),
  list: (params) => api.get('/api/leaves', { params }),
  getMyLeaves: () => api.get('/api/leaves/my-leaves'), // New
  getBalance: () => api.get('/api/leaves/balance'), // New
  getRequestMutationWindowHours: () => api.get('/api/tenant/request-mutation-window-hours'),
  cancel: (leaveId, reason) => api.post(`/api/leaves/${leaveId}/cancel`, { reason }),
  edit: (leaveId, data) => api.put(`/api/leaves/${leaveId}/edit`, data),
  getCompOffs: () => api.get('/api/comp-off'),
  approveLevel1: (leaveId, comments) => api.post(`/api/leaves/${leaveId}/approve-level1`, { comments }),
  approveLevel2: (leaveId, comments) => api.post(`/api/leaves/${leaveId}/approve-level2`, { comments }),
  reject: (leaveId, rejectionReason) => api.post(`/api/leaves/${leaveId}/reject`, { rejection_reason: rejectionReason }),
  escalate: (leaveId, comments) => api.post(`/api/leaves/${leaveId}/escalate`, { comments }),
  applyOnBehalf: (data) => api.post('/api/leaves/apply-on-behalf', data),
  
  // Leave policies
  getPolicies: () => api.get('/api/leave-policies'),
  getPolicy: (policyId) => api.get(`/api/leave-policies/${policyId}`),
  createPolicy: (data) => api.post('/api/leave-policies', data),
  updatePolicy: (policyId, data) => api.put(`/api/leave-policies/${policyId}`, data),
  deletePolicy: (policyId) => api.delete(`/api/leave-policies/${policyId}`),
  
  // Public holidays
  getHolidays: (year) =>
    api.get('/api/holidays', {
      params: Number.isInteger(year) ? { year } : {},
    }),
  createHoliday: (data) => api.post('/api/holidays', data),
  updateHoliday: (holidayId, data) => api.put(`/api/holidays/${holidayId}`, data),
  deleteHoliday: (holidayId) => api.delete(`/api/holidays/${holidayId}`),
  
  // Carry forward
  processCarryForward: (fromYear, toYear) => api.post('/api/leave-carry-forward/process', { from_year: fromYear, to_year: toYear }),
  
  // Short leave
  applyShortLeave: (data) => api.post('/api/short-leave/apply', data),
  listShortLeaves: (params) => api.get('/api/short-leave', { params }),
  approveShortLeave: (leaveId) => api.post(`/api/short-leave/${leaveId}/approve`),
  rejectShortLeave: (leaveId, reason) => api.post(`/api/short-leave/${leaveId}/reject`, { reason }),
};

export const assetsAPI = {
  list: (params) => api.get('/api/assets', { params }),
  listMy: () => api.get('/api/assets/my'),
  get: (assetId) => api.get(`/api/assets/${assetId}`),
  create: (data) => api.post('/api/assets', data),
  update: (assetId, data) => api.put(`/api/assets/${assetId}`, data),
  assign: (assetId, data) => api.post(`/api/assets/${assetId}/assign`, data),
  return: (assetId, data) => api.post(`/api/assets/${assetId}/return`, data),
  retire: (assetId, data) => api.post(`/api/assets/${assetId}/retire`, data),
  events: (assetId, params) => api.get(`/api/assets/${assetId}/events`, { params }),
};

export const esiAPI = {
  getPolicy: (params) => api.get('/api/esi/policy', { params }),
  createPolicy: (data) => api.post('/api/esi/policy', data),
  updatePolicy: (policyId, data) => api.put(`/api/esi/policy/${policyId}`, data),
  listBranches: (params) => api.get('/api/esi/branches', { params }),
  createBranch: (data) => api.post('/api/esi/branches', data),
  updateBranch: (branchId, data) => api.put(`/api/esi/branches/${branchId}`, data),
  getEmployeeProfile: (employeeId) => api.get(`/api/esi/employees/${employeeId}/profile`),
  upsertEmployeeProfile: (employeeId, data) => api.put(`/api/esi/employees/${employeeId}/profile`, data),
  getMyProfile: () => api.get('/api/esi/me/profile'),
  generateContributions: (params) => api.post('/api/esi/contributions/generate', null, { params }),
  listContributions: (params) => api.get('/api/esi/contributions', { params }),
  generateChallan: (params) => api.post('/api/esi/challans/generate', null, { params }),
  listChallans: (params) => api.get('/api/esi/challans', { params }),
  exportContributionRegisterCsvUrl: (params) => {
    const search = new URLSearchParams(params || {}).toString();
    return `/api/esi/reports/contribution-register.csv${search ? `?${search}` : ''}`;
  },
};

export const attendanceRulesAPI = {
  getShifts: () => api.get('/api/admin/shifts'),
  createShift: (data) => api.post('/api/admin/shifts', data),
  updateShift: (id, data) => api.put(`/api/admin/shifts/${id}`, data),
  deleteShift: (id) => api.delete(`/api/admin/shifts/${id}`),
  
  getRules: () => api.get('/api/admin/attendance-rules'),
  createRule: (data) => api.post('/api/admin/attendance-rules', data),
  updateRule: (id, data) => api.put(`/api/admin/attendance-rules/${id}`, data),
  deleteRule: (id) => api.delete(`/api/admin/attendance-rules/${id}`),
  
  processDaily: (data) => api.post('/api/admin/attendance/process', data),
  getSummary: (employeeId, month, year, approvalStatus) => api.get('/api/admin/attendance/summary', { params: { employee_id: employeeId, month, year, approval_status: approvalStatus } }),
  updateSummary: (summaryId, data) => api.put(`/api/admin/attendance/summary/${summaryId}`, data),
};

export const onboardingAPI = {
  getDocumentTypes: () => api.get('/api/document-types'),
  createDocumentType: (data) => api.post('/api/document-types', data),
  updateDocumentType: (docTypeId, data) => api.put(`/api/document-types/${docTypeId}`, data),
  uploadDocument: (employeeId, formData, params) => api.post(`/api/employees/${employeeId}/documents`, formData, { 
    headers: { 'Content-Type': 'multipart/form-data' },
    params 
  }),
  getEmployeeDocuments: (employeeId) => api.get(`/api/employees/${employeeId}/documents`),
  verifyDocument: (employeeId, docId, action = 'verify', rejected_reason = null) =>
    api.post(`/api/employees/${employeeId}/documents/${docId}/verify`, { rejected_reason }, { params: { action } }),
  getDocumentChecklistProgress: (employeeId) => api.get(`/api/employees/${employeeId}/documents/checklist-progress`),
  getDocumentVerificationQueue: (params) => api.get('/api/documents/verification-queue', { params }),
  bulkVerifyDocuments: (data) => api.put('/api/documents/bulk-verify', data),
  getDocumentExpiryAlerts: (days = 30) => api.get('/api/documents/expiry-alerts', { params: { days } }),
  
  getChecklists: (params) => api.get('/api/onboarding/checklists', { params }),
  createChecklist: (data) => api.post('/api/onboarding/checklists', data),
  updateChecklist: (checklistId, data) => api.put(`/api/onboarding/checklists/${checklistId}`, data),
  deleteChecklist: (checklistId) => api.delete(`/api/onboarding/checklists/${checklistId}`),
  
  getEmployeeOnboarding: (employeeId) => api.get(`/api/employees/${employeeId}/onboarding`),
  getEmployeeOnboardingWizard: (employeeId) => api.get(`/api/employees/${employeeId}/onboarding-wizard`),
  saveEmployeeOnboardingWizard: (employeeId, data) => api.put(`/api/employees/${employeeId}/onboarding-wizard`, data),
  startOnboarding: (employeeId, data) => api.post(`/api/employees/${employeeId}/onboarding/start`, data),
};

export const payrollAPI = {
  getSalaryStructures: () => api.get('/api/salary-structures'),
  createSalaryStructure: (data) => api.post('/api/salary-structures', data),
  updateSalaryStructure: (id, data) => api.put(`/api/salary-structures/${id}`, data),
  deleteSalaryStructure: (id) => api.delete(`/api/salary-structures/${id}`),
  
  getEmployeeSalary: (employeeId) => api.get(`/api/employees/${employeeId}/salary`),
  getSalaryHistory: (employeeId) => api.get(`/api/employees/${employeeId}/salary-history`),
  configureEmployeeSalary: (employeeId, data) => api.post(`/api/employees/${employeeId}/salary`, data),
  
  listPayrolls: (year) => api.get('/api/payroll', { params: { year } }),
  processPayroll: (month, year) => api.post('/api/payroll/process', { month, year }),
  approvePayroll: (payrollId) => api.post(`/api/payroll/${payrollId}/approve`),
  // Payslips are mailed only once the run is approved — approval is what freezes it.
  payslipReadiness: (month, year) => api.get('/api/payroll/payslips/readiness', { params: { month, year } }),
  sendPayslips: (month, year) => api.post('/api/payroll/payslips/send', { month, year }),
  getPayrollSchedule: () => api.get('/api/payroll/schedule'),
  savePayrollSchedule: (data) => api.put('/api/payroll/schedule', data),
  
  listPayslips: (params) => api.get('/api/payslips', { params }),
  getPayslipDetail: (id) => api.get(`/api/payslips/${id}`),
  
  // Tax Slabs
  getTaxSlabs: () => api.get('/api/payroll/tax-slabs'),
  createTaxSlab: (data) => api.post('/api/payroll/tax-slabs', data),
  updateTaxSlab: (id, data) => api.put(`/api/payroll/tax-slabs/${id}`, data),
  deleteTaxSlab: (id) => api.delete(`/api/payroll/tax-slabs/${id}`),

  // Reimbursements
  getReimbursements: () => api.get('/api/payroll/reimbursements'),
  createReimbursement: (data) => api.post('/api/payroll/reimbursements', data),
  updateReimbursementStatus: (id, status, comments) => api.put(`/api/payroll/reimbursements/${id}/status`, { status, comments }),
};

export const benefitsAPI = {
  getPlans: () => api.get('/api/benefits/plans'),
  createPlan: (data) => api.post('/api/benefits/plans', data),
  updatePlan: (id, data) => api.put(`/api/benefits/plans/${id}`, data),
  deletePlan: (id) => api.delete(`/api/benefits/plans/${id}`),
  enroll: (data) => api.post('/api/benefits/enroll', data),
  getMyBenefits: () => api.get('/api/benefits/my-benefits'),
  getEnrollments: () => api.get('/api/benefits/enrollments'),
};

// Education Suite — Faculty Management (add-on; endpoints are server-gated by the
// edu_faculty module key + education_suite add-on).
export const eduFacultyAPI = {
  list: (params) => api.get('/api/edu/faculty', { params }),
  getMine: () => api.get('/api/edu/faculty/me'),
  get: (id) => api.get(`/api/edu/faculty/${id}`),
  create: (data) => api.post('/api/edu/faculty', data),
  update: (id, data) => api.put(`/api/edu/faculty/${id}`, data),
  remove: (id) => api.delete(`/api/edu/faculty/${id}`),
  addCommittee: (id, data) => api.post(`/api/edu/faculty/${id}/committees`, data),
  removeCommittee: (id, committeeId) => api.delete(`/api/edu/faculty/${id}/committees/${committeeId}`),
  addMentorGroup: (id, data) => api.post(`/api/edu/faculty/${id}/mentor-groups`, data),
  removeMentorGroup: (id, groupId) => api.delete(`/api/edu/faculty/${id}/mentor-groups/${groupId}`),
};

// Connector framework — Slack + dev tools (gated by the `integrations` module).
export const integrationsAPI = {
  listEvents: () => api.get('/api/integrations/events'),
  listConnections: () => api.get('/api/integrations/connections'),
  getConnection: (id) => api.get(`/api/integrations/connections/${id}`),
  createConnection: (data) => api.post('/api/integrations/connections', data),
  updateConnection: (id, data) => api.put(`/api/integrations/connections/${id}`, data),
  deleteConnection: (id) => api.delete(`/api/integrations/connections/${id}`),
  testConnection: (id) => api.post(`/api/integrations/connections/${id}/test`),
  logs: (id) => api.get(`/api/integrations/connections/${id}/logs`),
};

// OKR auto-progress: link a key result to a GitHub/Jira/Linear source (gated by `integrations`).
export const okrSourcesAPI = {
  list: () => api.get('/api/okr-sources'),
  create: (data) => api.post('/api/okr-sources', data),
  remove: (id) => api.delete(`/api/okr-sources/${id}`),
  sync: (id) => api.post(`/api/okr-sources/${id}/sync`),
  listOkrs: () => api.get('/api/performance/okrs'),
};

// ESOP / RSU equity grants & vesting (gated by the `equity` module).
export const equityAPI = {
  listGrants: (params) => api.get('/api/equity/grants', { params }),
  getGrant: (id) => api.get(`/api/equity/grants/${id}`),
  createGrant: (data) => api.post('/api/equity/grants', data),
  updateGrant: (id, data) => api.put(`/api/equity/grants/${id}`, data),
  deleteGrant: (id) => api.delete(`/api/equity/grants/${id}`),
  terminateGrant: (id, terminationDate) => api.post(`/api/equity/grants/${id}/terminate`, { termination_date: terminationDate }),
  summary: () => api.get('/api/equity/summary'),
  myEquity: () => api.get('/api/equity/me'),
};

// Multi-entity / multi-BU organization masters (gated by the `multi_entity` module).
export const legalEntitiesAPI = {
  list: (params) => api.get('/api/org/legal-entities', { params }),
  get: (id) => api.get(`/api/org/legal-entities/${id}`),
  create: (data) => api.post('/api/org/legal-entities', data),
  update: (id, data) => api.put(`/api/org/legal-entities/${id}`, data),
  remove: (id) => api.delete(`/api/org/legal-entities/${id}`),
  importItems: (items) => api.post('/api/org/legal-entities/import', { items }),
  bulkStatus: (ids, isActive) => api.post('/api/org/legal-entities/bulk-status', { ids, is_active: isActive }),
  exportCsv: () => api.get('/api/org/legal-entities/export', { responseType: 'blob' }),
  countries: () => api.get('/api/org/legal-entities/countries'),
};

export const businessUnitsAPI = {
  list: (params) => api.get('/api/org/business-units', { params }),
  get: (id) => api.get(`/api/org/business-units/${id}`),
  create: (data) => api.post('/api/org/business-units', data),
  update: (id, data) => api.put(`/api/org/business-units/${id}`, data),
  remove: (id) => api.delete(`/api/org/business-units/${id}`),
  importItems: (items) => api.post('/api/org/business-units/import', { items }),
  bulkStatus: (ids, isActive) => api.post('/api/org/business-units/bulk-status', { ids, is_active: isActive }),
  exportCsv: () => api.get('/api/org/business-units/export', { responseType: 'blob' }),
};

// Enterprise SSO (SAML) + SCIM provisioning admin (gated by the `sso` module).
export const enterpriseSsoAPI = {
  getSso: () => api.get('/api/enterprise/sso'),
  updateSso: (data) => api.put('/api/enterprise/sso', data),
  listScimTokens: () => api.get('/api/enterprise/scim-tokens'),
  createScimToken: (name) => api.post('/api/enterprise/scim-tokens', { name }),
  revokeScimToken: (id) => api.delete(`/api/enterprise/scim-tokens/${id}`),
};

// Education Suite — Organization Masters (Schools & Departments). Bundled into the
// edu_faculty module gate; faculty reference these by FK.
export const eduSchoolsAPI = {
  list: (params) => api.get('/api/edu/schools', { params }),
  get: (id) => api.get(`/api/edu/schools/${id}`),
  create: (data) => api.post('/api/edu/schools', data),
  update: (id, data) => api.put(`/api/edu/schools/${id}`, data),
  remove: (id) => api.delete(`/api/edu/schools/${id}`),
  importItems: (items) => api.post('/api/edu/schools/import', { items }),
  bulkStatus: (ids, isActive) => api.post('/api/edu/schools/bulk-status', { ids, is_active: isActive }),
  exportCsv: () => api.get('/api/edu/schools/export', { responseType: 'blob' }),
};

export const eduDepartmentsAPI = {
  list: (params) => api.get('/api/edu/departments', { params }),
  get: (id) => api.get(`/api/edu/departments/${id}`),
  create: (data) => api.post('/api/edu/departments', data),
  update: (id, data) => api.put(`/api/edu/departments/${id}`, data),
  remove: (id) => api.delete(`/api/edu/departments/${id}`),
  importItems: (items) => api.post('/api/edu/departments/import', { items }),
  bulkStatus: (ids, isActive) => api.post('/api/edu/departments/bulk-status', { ids, is_active: isActive }),
  exportCsv: () => api.get('/api/edu/departments/export', { responseType: 'blob' }),
};

export const eduCalendarAPI = {
  listYears: () => api.get('/api/edu/calendar/years'),
  createYear: (data) => api.post('/api/edu/calendar/years', data),
  updateYear: (id, data) => api.put(`/api/edu/calendar/years/${id}`, data),
  deleteYear: (id) => api.delete(`/api/edu/calendar/years/${id}`),
  listTerms: (academicYearId) => api.get('/api/edu/calendar/terms', { params: academicYearId ? { academic_year_id: academicYearId } : {} }),
  createTerm: (data) => api.post('/api/edu/calendar/terms', data),
  updateTerm: (id, data) => api.put(`/api/edu/calendar/terms/${id}`, data),
  deleteTerm: (id) => api.delete(`/api/edu/calendar/terms/${id}`),
  listEvents: (termId) => api.get('/api/edu/calendar/events', { params: termId ? { term_id: termId } : {} }),
  createEvent: (data) => api.post('/api/edu/calendar/events', data),
  updateEvent: (id, data) => api.put(`/api/edu/calendar/events/${id}`, data),
  deleteEvent: (id) => api.delete(`/api/edu/calendar/events/${id}`),
  blockedDates: (termId) => api.get('/api/edu/calendar/blocked-dates', { params: termId ? { term_id: termId } : {} }),
};

export const eduTimetableAPI = {
  listCourses: (params) => api.get('/api/edu/timetable/courses', { params }),
  createCourse: (data) => api.post('/api/edu/timetable/courses', data),
  updateCourse: (id, data) => api.put(`/api/edu/timetable/courses/${id}`, data),
  deleteCourse: (id) => api.delete(`/api/edu/timetable/courses/${id}`),
  importCourses: (items) => api.post('/api/edu/timetable/courses/import', { items }),
  listRooms: (params) => api.get('/api/edu/timetable/rooms', { params }),
  createRoom: (data) => api.post('/api/edu/timetable/rooms', data),
  updateRoom: (id, data) => api.put(`/api/edu/timetable/rooms/${id}`, data),
  deleteRoom: (id) => api.delete(`/api/edu/timetable/rooms/${id}`),
  importRooms: (items) => api.post('/api/edu/timetable/rooms/import', { items }),
  listSlots: (params) => api.get('/api/edu/timetable/slots', { params }),
  createSlot: (data) => api.post('/api/edu/timetable/slots', data),
  deleteSlot: (id) => api.delete(`/api/edu/timetable/slots/${id}`),
  substitute: (id, data) => api.post(`/api/edu/timetable/slots/${id}/substitute`, data),
  checkConflicts: (data) => api.post('/api/edu/timetable/check-conflicts', data),
  listNorms: () => api.get('/api/edu/workload/norms'),
  upsertNorm: (data) => api.put('/api/edu/workload/norms', data),
  workloadSummary: (termId) => api.get('/api/edu/workload/summary', { params: { term_id: termId } }),
};

export const eduContractsAPI = {
  list: (params) => api.get('/api/edu/contracts', { params }),
  expiring: (days = 60) => api.get('/api/edu/contracts/expiring', { params: { days } }),
  get: (id) => api.get(`/api/edu/contracts/${id}`),
  create: (data) => api.post('/api/edu/contracts', data),
  update: (id, data) => api.put(`/api/edu/contracts/${id}`, data),
  remove: (id) => api.delete(`/api/edu/contracts/${id}`),
  renew: (id) => api.post(`/api/edu/contracts/${id}/renew`),
};

export const eduAccreditationAPI = {
  listFrameworks: (params) => api.get('/api/edu/accreditation/frameworks', { params }),
  createFramework: (data) => api.post('/api/edu/accreditation/frameworks', data),
  updateFramework: (id, data) => api.put(`/api/edu/accreditation/frameworks/${id}`, data),
  deleteFramework: (id) => api.delete(`/api/edu/accreditation/frameworks/${id}`),
  listMetrics: (frameworkId) => api.get(`/api/edu/accreditation/frameworks/${frameworkId}/metrics`),
  createMetric: (frameworkId, data) => api.post(`/api/edu/accreditation/frameworks/${frameworkId}/metrics`, data),
  updateMetric: (id, data) => api.put(`/api/edu/accreditation/metrics/${id}`, data),
  deleteMetric: (id) => api.delete(`/api/edu/accreditation/metrics/${id}`),
  readiness: (frameworkId) => api.get(`/api/edu/accreditation/frameworks/${frameworkId}/readiness`),
  export: (frameworkId) => api.get(`/api/edu/accreditation/frameworks/${frameworkId}/export`),
};

export const eduResearchAPI = {
  listOutputs: (params) => api.get('/api/edu/research/outputs', { params }),
  createOutput: (data) => api.post('/api/edu/research/outputs', data),
  updateOutput: (id, data) => api.put(`/api/edu/research/outputs/${id}`, data),
  deleteOutput: (id) => api.delete(`/api/edu/research/outputs/${id}`),
  listResearcherIds: (params) => api.get('/api/edu/research/researcher-ids', { params }),
  upsertResearcherIds: (data) => api.put('/api/edu/research/researcher-ids', data),
  annualReport: (params) => api.get('/api/edu/research/annual-report', { params }),
};

export const eduGrievanceAPI = {
  list: (params) => api.get('/api/edu/grievance', { params }),
  get: (id) => api.get(`/api/edu/grievance/${id}`),
  create: (data) => api.post('/api/edu/grievance', data),
  assign: (id, data) => api.post(`/api/edu/grievance/${id}/assign`, data),
  escalate: (id, data) => api.post(`/api/edu/grievance/${id}/escalate`, data),
  resolve: (id, data) => api.post(`/api/edu/grievance/${id}/resolve`, data),
  track: (token) => api.get(`/api/edu/grievance/track/${token}`),
  addAttachment: (id, data) => api.post(`/api/edu/grievance/${id}/attachments`, data),
};

export const eduStudentWorkerAPI = {
  list: (params) => api.get('/api/edu/student-workers', { params }),
  get: (id) => api.get(`/api/edu/student-workers/${id}`),
  create: (data) => api.post('/api/edu/student-workers', data),
  update: (id, data) => api.put(`/api/edu/student-workers/${id}`, data),
  remove: (id) => api.delete(`/api/edu/student-workers/${id}`),
  listAssignments: (id) => api.get(`/api/edu/student-workers/${id}/assignments`),
  addAssignment: (id, data) => api.post(`/api/edu/student-workers/${id}/assignments`, data),
  removeAssignment: (assignmentId) => api.delete(`/api/edu/student-workers/assignments/${assignmentId}`),
};

export const eduAdjunctAPI = {
  list: (params) => api.get('/api/edu/adjunct/appointments', { params }),
  get: (id) => api.get(`/api/edu/adjunct/appointments/${id}`),
  create: (data) => api.post('/api/edu/adjunct/appointments', data),
  update: (id, data) => api.put(`/api/edu/adjunct/appointments/${id}`, data),
  remove: (id) => api.delete(`/api/edu/adjunct/appointments/${id}`),
  renew: (id) => api.post(`/api/edu/adjunct/appointments/${id}/renew`),
};

export const eduIntegrationsAPI = {
  list: (params) => api.get('/api/edu/integrations', { params }),
  get: (provider) => api.get(`/api/edu/integrations/${provider}`),
  connect: (provider, data) => api.post(`/api/edu/integrations/${provider}/connect`, data),
  test: (provider, data) => api.post(`/api/edu/integrations/${provider}/test`, data || {}),
  sync: (provider) => api.post(`/api/edu/integrations/${provider}/sync`),
  remove: (provider) => api.delete(`/api/edu/integrations/${provider}`),
};

export const reportsAPI = {
  employeeReport: (params) => api.get('/api/reports/employees', { params }),
  attendanceReport: (params) => api.get('/api/reports/attendance', { params }),
  musterRegister: (params) => api.get('/api/reports/muster-register', { params }),
  leaveReport: (params) => api.get('/api/reports/leave', { params }),
  payrollReport: (params) => api.get('/api/reports/payroll', { params }),
  leaveBalanceReport: (params) => api.get('/api/reports/leave-balance', { params }),
  statutoryComplianceReport: (params) => api.get('/api/reports/statutory-compliance', { params }),
  performanceReport: (params) => api.get('/api/reports/performance', { params }),
  exportPerformanceReport: (params) =>
    api.get('/api/reports/performance/export', { params, responseType: 'blob' }),
  myPerformanceReport: (params) => api.get('/api/reports/my-performance', { params }),
  exportMyPerformanceReport: (params) =>
    api.get('/api/reports/my-performance/export', { params, responseType: 'blob' }),
};

export const helpdeskAPI = {
  createTicket: (data) => api.post('/api/helpdesk/tickets', data),
  getTickets: (params) => api.get('/api/helpdesk/tickets', { params }),
  exportTickets: (params) => api.get('/api/helpdesk/export', { params, responseType: 'blob' }),
  getCategories: (params) => api.get('/api/helpdesk/categories', { params }),
  createCategory: (data) => api.post('/api/helpdesk/categories', data),
  updateCategory: (categoryId, data) => api.patch(`/api/helpdesk/categories/${categoryId}`, data),
  getTicketDetail: (ticketId) => api.get(`/api/helpdesk/tickets/${ticketId}`),
  updateTicket: (ticketId, data) => api.patch(`/api/helpdesk/tickets/${ticketId}`, data),
  addComment: (ticketId, data) => api.post(`/api/helpdesk/tickets/${ticketId}/comments`, data),
  uploadAttachment: (ticketId, formData) =>
    api.post(`/api/helpdesk/tickets/${ticketId}/attachments`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  getAttachmentDownloadUrl: (attachmentId, token) =>
    `${api.defaults.baseURL || ''}/api/helpdesk/attachments/${attachmentId}/download?token=${encodeURIComponent(token)}`,
  getTicketAudit: (ticketId) => api.get(`/api/helpdesk/tickets/${ticketId}/audit`),
  getBoard: () => api.get('/api/helpdesk/board'),
  getDashboardSummary: () => api.get('/api/helpdesk/dashboard/summary'),
  escalateTicket: (ticketId, data) => api.put(`/api/helpdesk/tickets/${ticketId}/escalate`, data),
  getSlaPolicies: () => api.get('/api/helpdesk/sla-policies'),
  updateSlaPolicies: (policies) => api.put('/api/helpdesk/sla-policies', policies),
};

export const gamificationAPI = {
  getModule: () => api.get('/api/gamification/module'),
  getDashboard: (params) => api.get('/api/gamification/dashboard', { params }),
  getAnalyticsDashboard: () => api.get('/api/gamification/analytics'),
  getNavigation: () => api.get('/api/gamification/navigation'),
  getAchievementSystem: () => api.get('/api/gamification/achievement-system'),
  getAchievementSystemSources: () => api.get('/api/gamification/achievement-system/sources'),
  processAchievementSystem: (data) => api.post('/api/gamification/achievement-system/process', data),
  getRewardMarketplace: () => api.get('/api/gamification/reward-marketplace'),
  getRewardMarketplaceCategories: () => api.get('/api/gamification/reward-marketplace/categories'),
  redeemRewardMarketplaceItem: (data) => api.post('/api/gamification/reward-marketplace/redemptions', data),
  approveRewardMarketplaceRedemption: (redemptionId, data) => api.post(`/api/gamification/reward-marketplace/redemptions/${redemptionId}/approve`, data),
  rejectRewardMarketplaceRedemption: (redemptionId, data) => api.post(`/api/gamification/reward-marketplace/redemptions/${redemptionId}/reject`, data),
  getAmazonVoucherEngine: () => api.get('/api/gamification/amazon-voucher-engine'),
  updateAmazonVoucherEngineControls: (data) => api.put('/api/gamification/amazon-voucher-engine/controls', data),
  redeemAmazonVoucher: (data) => api.post('/api/gamification/amazon-voucher-engine/redemptions', data),
  approveAmazonVoucherRedemption: (redemptionId, data) => api.post(`/api/gamification/amazon-voucher-engine/redemptions/${redemptionId}/approve`, data),
  rejectAmazonVoucherRedemption: (redemptionId, data) => api.post(`/api/gamification/amazon-voucher-engine/redemptions/${redemptionId}/reject`, data),
  issueAmazonVoucherRedemption: (redemptionId, data) => api.post(`/api/gamification/amazon-voucher-engine/redemptions/${redemptionId}/issue`, data),
  getLeaderboardSystem: () => api.get('/api/gamification/leaderboard-system'),
  getLeaderboardSystemOptions: () => api.get('/api/gamification/leaderboard-system/options'),
  getLevelSystem: () => api.get('/api/gamification/level-system'),
  getLevelSystemSources: () => api.get('/api/gamification/level-system/sources'),
  getLevelDefinitions: () => api.get('/api/gamification/level-system/levels'),
  createLevelDefinition: (data) => api.post('/api/gamification/level-system/levels', data),
  updateLevelDefinition: (levelId, data) => api.patch(`/api/gamification/level-system/levels/${levelId}`, data),
  getLevelActivityRules: () => api.get('/api/gamification/level-system/activity-rules'),
  createLevelActivityRule: (data) => api.post('/api/gamification/level-system/activity-rules', data),
  updateLevelActivityRule: (ruleId, data) => api.patch(`/api/gamification/level-system/activity-rules/${ruleId}`, data),
  processLevelSystem: (data) => api.post('/api/gamification/level-system/process', data),
  getRankTitles: () => api.get('/api/gamification/level-system/rank-titles'),
  createRankTitle: (data) => api.post('/api/gamification/level-system/rank-titles', data),
  updateRankTitle: (rankTitleId, data) => api.patch(`/api/gamification/level-system/rank-titles/${rankTitleId}`, data),
  getRankRecommendations: () => api.get('/api/gamification/level-system/rank-recommendations'),
  generateRankRecommendations: () => api.post('/api/gamification/level-system/rank-recommendations/generate'),
  applyRankRecommendation: (recommendationId) => api.post(`/api/gamification/level-system/rank-recommendations/${recommendationId}/apply`),
  getAIGamificationEngine: () => api.get('/api/gamification/ai-engine'),
  generateAIGamificationEngine: () => api.post('/api/gamification/ai-engine/generate'),
  getChallengeCatalog: (params) => api.get('/api/gamification/challenge-catalog', { params }),
  createChallengeCatalog: (data) => api.post('/api/gamification/challenge-catalog', data),
  updateChallengeCatalog: (catalogId, data) => api.patch(`/api/gamification/challenge-catalog/${catalogId}`, data),
  getChallengeCycles: (params) => api.get('/api/gamification/challenge-cycles', { params }),
  getMyChallengeCycles: () => api.get('/api/gamification/challenge-cycles/my'),
  generateChallengeCycles: (data) => api.post('/api/gamification/challenge-cycles/generate', data),
  processChallengeCycles: (data) => api.post('/api/gamification/challenge-cycles/process', data),
  getRoleActivityCatalog: (params) => api.get('/api/gamification/role-activities/catalog', { params }),
  getRoleActivities: (params) => api.get('/api/gamification/role-activities', { params }),
  generateRoleActivities: (data) => api.post('/api/gamification/role-activities/generate', data),
  getSectionRecords: (sectionKey) => api.get(`/api/gamification/sections/${sectionKey}`),
  createSectionRecord: (sectionKey, data) => api.post(`/api/gamification/sections/${sectionKey}`, data),
  updateSectionRecord: (sectionKey, recordId, data) => api.patch(`/api/gamification/sections/${sectionKey}/${recordId}`, data),
  getSettings: () => api.get('/api/gamification/settings'),
  updateSettings: (data) => api.put('/api/gamification/settings', data),
};

export const performanceAPI = {
  getMyKpi: (params) => api.get('/api/performance/my-kpi', { params }),
  getAdminOverview: (params) => api.get('/api/performance/admin/overview', { params }),
  getEmployeeDetail: (employeeId, params) => api.get(`/api/performance/admin/employee/${employeeId}/detail`, { params }),
  upsertKpi: (data) => api.post('/api/performance/kpis', data),
  submitFeedback360: (data) => api.post('/api/performance/feedback-360', data),
};

export const pulseSurveyAPI = {
  getMyInbox: () => api.get('/api/pulse-surveys/my-inbox'),
  getMyDraft: (surveyId) => api.get(`/api/pulse-surveys/${surveyId}/my-draft`),
  saveMyDraft: (surveyId, data) => api.put(`/api/pulse-surveys/${surveyId}/my-draft`, data),
  listSurveys: (params) => api.get('/api/pulse-surveys', { params }),
  createSurvey: (data) => api.post('/api/pulse-surveys', data),
  getSurveyDetail: (surveyId) => api.get(`/api/pulse-surveys/${surveyId}`),
  addQuestion: (surveyId, data) => api.post(`/api/pulse-surveys/${surveyId}/questions`, data),
  publishSurvey: (surveyId) => api.post(`/api/pulse-surveys/${surveyId}/publish`),
  listRecipients: (surveyId) => api.get(`/api/pulse-surveys/${surveyId}/recipients`),
  submitResponse: (surveyId, data) => api.post(`/api/pulse-surveys/${surveyId}/responses`, data),
  listResponses: (surveyId) => api.get(`/api/pulse-surveys/${surveyId}/responses`),
  listAnswers: (surveyId) => api.get(`/api/pulse-surveys/${surveyId}/answers`),
  getAnalytics: (surveyId) => api.get(`/api/pulse-surveys/${surveyId}/analytics`),
  listActionPlans: (surveyId) => api.get(`/api/pulse-surveys/${surveyId}/action-plans`),
  createActionPlan: (surveyId, data) => api.post(`/api/pulse-surveys/${surveyId}/action-plans`, data),
  updateActionPlan: (surveyId, actionPlanId, data) =>
    api.patch(`/api/pulse-surveys/${surveyId}/action-plans/${actionPlanId}`, data),
};

export const executiveAnalyticsAPI = {
  getSummary: (params) => api.get('/api/analytics/executive/summary', { params }),
  getTrends: (params) => api.get('/api/analytics/executive/trends', { params }),
  getDrilldown: (params) => api.get('/api/analytics/executive/drilldown', { params }),
  createExportPack: (data) => api.post('/api/analytics/executive/export-pack', data),
  listExportPacks: (params) => api.get('/api/analytics/executive/export-packs', { params }),
  downloadExportPack: (packId) =>
    api.get(`/api/analytics/executive/export-packs/${packId}/download`, { responseType: 'blob' }),
  createSnapshot: (data) => api.post('/api/analytics/executive/snapshots', data),
};

export const governanceAPI = {
  getPolicy: () => api.get('/api/governance/policy'),
  listGlobalMasters: (params) => api.get('/api/governance/global-masters', { params }),
  createGlobalMaster: (data) => api.post('/api/governance/global-masters', data),
  updateGlobalMaster: (masterId, data) => api.patch(`/api/governance/global-masters/${masterId}`, data),
  listPublishedGlobalMasters: (params) => api.get('/api/governance/global-masters/published', { params }),
  listTenantMasters: (params) => api.get('/api/governance/tenant-masters', { params }),
  createMasterChangeRequest: (data) => api.post('/api/governance/master-change-requests', data),
  listMasterChangeRequests: (params) => api.get('/api/governance/master-change-requests', { params }),
  approveMasterChangeRequest: (requestId, data) =>
    api.post(`/api/governance/master-change-requests/${requestId}/approve`, data),
  rejectMasterChangeRequest: (requestId, data) =>
    api.post(`/api/governance/master-change-requests/${requestId}/reject`, data),
};

export const workRequestAPI = {
  listPolicies: () => api.get('/api/work-requests/policies'),
  updatePolicy: (requestType, data) => api.put(`/api/work-requests/policies/${requestType}`, data),

  applyRegularization: (data) => api.post('/api/work-requests/regularization/apply', data),
  applyRegularizationOnBehalf: (data) => api.post('/api/work-requests/regularization/apply-on-behalf', data),
  getMyRegularizations: (params) => api.get('/api/work-requests/regularization/my', { params }),
  getTeamRegularizations: (params) => api.get('/api/work-requests/regularization/team', { params }),
  approveRegularization: (requestId, data) => api.post(`/api/work-requests/regularization/${requestId}/approve`, data || {}),
  rejectRegularization: (requestId, data) => api.post(`/api/work-requests/regularization/${requestId}/reject`, data),
  cancelRegularization: (requestId, reason) => api.post(`/api/work-requests/regularization/${requestId}/cancel`, { reason }),
  editRegularization: (requestId, data) => api.put(`/api/work-requests/regularization/${requestId}/edit`, data),

  applyWfh: (data) => api.post('/api/work-requests/wfh/apply', data),
  applyWfhOnBehalf: (data) => api.post('/api/work-requests/wfh/apply-on-behalf', data),
  getMyWfh: (params) => api.get('/api/work-requests/wfh/my', { params }),
  getTeamWfh: (params) => api.get('/api/work-requests/wfh/team', { params }),
  approveWfh: (requestId, data) => api.post(`/api/work-requests/wfh/${requestId}/approve`, data || {}),
  rejectWfh: (requestId, data) => api.post(`/api/work-requests/wfh/${requestId}/reject`, data),
  cancelWfh: (requestId, reason) => api.post(`/api/work-requests/wfh/${requestId}/cancel`, { reason }),
  editWfh: (requestId, data) => api.put(`/api/work-requests/wfh/${requestId}/edit`, data),

  applyOutstation: (data) => api.post('/api/work-requests/outstation/apply', data),
  applyOutstationOnBehalf: (data) => api.post('/api/work-requests/outstation/apply-on-behalf', data),
  getMyOutstation: (params) => api.get('/api/work-requests/outstation/my', { params }),
  getTeamOutstation: (params) => api.get('/api/work-requests/outstation/team', { params }),
  approveOutstation: (requestId, data) => api.post(`/api/work-requests/outstation/${requestId}/approve`, data || {}),
  rejectOutstation: (requestId, data) => api.post(`/api/work-requests/outstation/${requestId}/reject`, data),
  cancelOutstation: (requestId, reason) => api.post(`/api/work-requests/outstation/${requestId}/cancel`, { reason }),
  editOutstation: (requestId, data) => api.put(`/api/work-requests/outstation/${requestId}/edit`, data),
};

export const departmentMasterAPI = {
  list: (params) => api.get('/api/department-masters', { params }),
  listOptions: () => api.get('/api/department-masters/options'),
  createDepartment: (data) => api.post('/api/department-masters', data),
  updateDepartment: (departmentId, data) => api.put(`/api/department-masters/${departmentId}`, data),
  deleteDepartment: (departmentId) => api.delete(`/api/department-masters/${departmentId}`),
  bootstrapITCatalog: () => api.post('/api/department-masters/bootstrap-it-catalog'),
  createSubDepartment: (departmentId, data) =>
    api.post(`/api/department-masters/${departmentId}/sub-departments`, data),
  updateSubDepartment: (subDepartmentId, data) =>
    api.put(`/api/sub-department-masters/${subDepartmentId}`, data),
  deleteSubDepartment: (subDepartmentId) => api.delete(`/api/sub-department-masters/${subDepartmentId}`),
  createDesignation: (data) => api.post('/api/designation-masters', data),
  updateDesignation: (designationId, data) => api.put(`/api/designation-masters/${designationId}`, data),
  deleteDesignation: (designationId) => api.delete(`/api/designation-masters/${designationId}`),
};

export const masterImportAPI = {
  listSpecs: () => api.get('/api/master-import/specs'),
  getSpec: (masterType) => api.get(`/api/master-import/specs/${masterType}`),
  downloadSampleCsvUrl: (masterType) => buildApiUrl(`/api/master-import/specs/${masterType}/sample.csv`),
  downloadSampleCsv: (masterType) => api.get(`/api/master-import/specs/${masterType}/sample.csv`, { responseType: 'blob' }),
  importJson: (masterType, rows) => api.post(`/api/master-import/${masterType}/json`, { rows }),
  importUpload: (masterType, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/api/master-import/${masterType}/upload`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

export const teamAPI = {
  // Reporting line, not department. `on` and `upcoming_days` are optional.
  getTeamStatus: (params) => api.get('/api/team/status', { params }),
};

export const workspaceAdminAPI = {
  getSettings: () => api.get('/workspace/settings'),
  updateSettings: (data) => api.put('/workspace/settings', data),
  resetSystemSettings: () => api.post('/workspace/settings/reset'),
  getSetupStatus: () => api.get('/workspace/setup'),
  seedDefaults: () => api.post('/workspace/setup/seed-defaults'),
  seedStandardLeavePolicies: (allTenants = false) =>
    api.post('/workspace/setup/seed-standard-leave-policies', null, { params: { all_tenants: allTenants } }),
  getAuditLogs: (params) => api.get('/audit-logs', { params }),
  listTenants: () => api.get('/tenants'),
  createTenant: (data) =>
    api.post('/tenants', {
      tenant_name: data.name,
      tenant_slug: data.slug,
      admin_email: data.admin_email,
      admin_password: data.admin_password || null,
      vertical: data.vertical || null,
    }),
  updateTenant: (tenantId, data) =>
    api.put(`/tenants/${tenantId}`, {
      name: data.name,
      slug: data.slug,
      status: data.status,
      vertical: data.vertical,
    }),
  assignTenantSubscription: (tenantId, data) => api.put(`/tenants/${tenantId}/subscription`, data),
  deleteTenant: (tenantId) => api.delete(`/tenants/${tenantId}`),
  // Everything the consent dialog shows: role-scoped reason codes, the target
  // tenant's consent state, the reserved session id and the restriction list.
  // Resolved server-side so the dialog cannot offer what proxy-login refuses.
  // Drives the proxy banner: session id, reason, region, audited action count
  // and the token's own remaining life.
  getProxySessionStatus: () => api.get('/tenants/proxy-session-status'),
  getProxySessionContext: (tenantSlug) =>
    api.get('/tenants/proxy-session-context', { params: { tenant_slug: tenantSlug } }),
  // reason_code / justification / duration_minutes are required server-side for
  // any real cross-tenant session; see routes_workspace_admin.proxy_login.
  proxyLogin: (tenantSelector, consent = {}) =>
    api.post('/tenants/proxy-login', { tenant_slug: tenantSelector, ...consent }),
  proxyExit: () => api.post('/tenants/proxy-exit'),
  // Locations of one's own organisation. Distinct from proxyLogin on purpose:
  // no consent dialog, no reason code, no justification — a company's own admin
  // opening their own branch is not impersonation. See backend/tenant_locations.
  listMyLocations: () => api.get('/tenants/my-locations'),
  switchLocation: (tenantSlug) => api.post('/tenants/switch-location', { tenant_slug: tenantSlug }),
  listProxyTargets: () => api.get('/tenants/proxy-targets'),
  getChildTenantsOverview: () => api.get('/tenants/children/overview'),
  getDashboardStats: () => api.get('/dashboard/stats'),
  getSubscriptionCatalog: () => api.get('/subscription/catalog'),
  bootstrapDefaultPlanAndSeed: () => api.post('/subscription/bootstrap-default-seed'),
  listSubscriptionPlans: (params) => api.get('/subscription/plans', { params }),
  createSubscriptionPlan: (data) => api.post('/subscription/plans', data),
  updateSubscriptionPlan: (planId, data) => api.put(`/subscription/plans/${planId}`, data),
  deleteSubscriptionPlan: (planId) => api.delete(`/subscription/plans/${planId}`),
  listGlobalSalaryStructures: () => api.get('/payroll/global/salary-structures'),
  createGlobalSalaryStructure: (data) => api.post('/payroll/global/salary-structures', data),
  updateGlobalSalaryStructure: (id, data) => api.put(`/payroll/global/salary-structures/${id}`, data),
  deleteGlobalSalaryStructure: (id) => api.delete(`/payroll/global/salary-structures/${id}`),
  listGlobalChecklists: () => api.get('/api/global/onboarding-checklists'),
  createGlobalChecklist: (data) => api.post('/api/global/onboarding-checklists', data),
  updateGlobalChecklist: (id, data) => api.put(`/api/global/onboarding-checklists/${id}`, data),
  deleteGlobalChecklist: (id) => api.delete(`/api/global/onboarding-checklists/${id}`),
  listGlobalShifts: () => api.get('/api/global/shifts'),
  createGlobalShift: (data) => api.post('/api/global/shifts', data),
  updateGlobalShift: (id, data) => api.put(`/api/global/shifts/${id}`, data),
  deleteGlobalShift: (id) => api.delete(`/api/global/shifts/${id}`),
  listGlobalBenefitPlans: () => api.get('/payroll/global/benefit-plans'),
  createGlobalBenefitPlan: (data) => api.post('/payroll/global/benefit-plans', data),
  updateGlobalBenefitPlan: (id, data) => api.put(`/payroll/global/benefit-plans/${id}`, data),
  deleteGlobalBenefitPlan: (id) => api.delete(`/payroll/global/benefit-plans/${id}`),
  listGlobalTaxSlabs: () => api.get('/payroll/global/tax-slabs'),
  createGlobalTaxSlab: (data) => api.post('/payroll/global/tax-slabs', data),
  updateGlobalTaxSlab: (id, data) => api.put(`/payroll/global/tax-slabs/${id}`, data),
  deleteGlobalTaxSlab: (id) => api.delete(`/payroll/global/tax-slabs/${id}`),
  // User management
  listUsers: () => api.get('/users'),
  createUser: (data) => api.post('/users', data),
  updateUser: (userId, data) => api.put(`/users/${userId}`, data),
  deleteUser: (userId) => api.delete(`/users/${userId}`),
  resetUserPassword: (userId) => api.post(`/users/${userId}/reset-password`),
  // Control-plane APIs
  getTenantTree: () => api.get('/api/control/tenants/tree'),
  getTenantHierarchy: () => api.get('/api/control/tenants/hierarchy'),
  getTenantTreePublic: () => api.get('/api/tenants/tree'),
  createTenantNode: (data) => api.post('/api/control/tenants', data),
  createSubtenant: (tenantId, data) => api.post(`/api/control/tenants/${tenantId}/subtenant`, data),
  createChildTenant: (tenantId, data) => api.post(`/api/tenants/${tenantId}/create-child`, data),
  updateTenantNode: (tenantId, data) => api.patch(`/api/control/tenants/${tenantId}`, data),
  upsertTenantMembership: (data) => api.post('/api/control/memberships', data),
  listTenantMemberships: (params) => api.get('/api/control/memberships', { params }),
  deleteTenantMembership: (membershipId) => api.delete(`/api/control/memberships/${membershipId}`),
  listPermissionGroups: (params) => api.get('/api/control/permission-groups', { params }),
  upsertPermissionGroup: (data) => api.post('/api/control/permission-groups', data),
  assignTenantPermissionGroups: (tenantId, data) =>
    api.put(`/api/control/tenants/${tenantId}/permission-groups`, data),
  getEffectiveRoles: (params) => api.get('/api/control/roles/effective', { params }),
  cloneTenantRole: (data) => api.post('/api/control/roles/clone', data),
  inheritTenantRole: (data) => api.post('/api/control/roles/inherit', data),
  restrictTenantRole: (data) => api.post('/api/control/roles/restrict', data),
  listBillingAccounts: () => api.get('/api/control/billing/accounts'),
  createBillingAccount: (data) => api.post('/api/control/billing/accounts', data),
  linkTenantBillingAccount: (billingAccountId, data) =>
    api.post(`/api/control/billing/accounts/${billingAccountId}/tenants/link`, data),
  collectUsageMetrics: (params) => api.post('/api/control/usage/collect', null, { params }),
  getUsageMetrics: (tenantId, params) => api.get(`/api/control/usage/${tenantId}`, { params }),
  createInvoice: (data) => api.post('/api/control/billing/invoices', data),
  listInvoices: (params) => api.get('/api/control/billing/invoices', { params }),
  bootstrapBillingDashboardSeed: () => api.post('/api/control/billing/bootstrap-dashboard-seed'),
  getBillingDashboard: () => api.get('/api/control/billing/dashboard'),
  listBillingSubscriptions: () => api.get('/api/control/billing/subscriptions'),
  listCollectedPayments: () => api.get('/api/control/billing/payments/collected'),
  listPendingPayments: () => api.get('/api/control/billing/payments/pending'),
  listDiscountOffers: () => api.get('/api/control/billing/discounts'),
  getTenantBranding: (tenantId) => api.get(`/api/control/tenants/${tenantId}/branding`),
  updateTenantBranding: (tenantId, data) => api.put(`/api/control/tenants/${tenantId}/branding`, data),
  getTenantVerifiedDomains: (tenantId) => api.get(`/api/control/tenants/${tenantId}/verified-domains`),
  updateTenantVerifiedDomains: (tenantId, data) => api.put(`/api/control/tenants/${tenantId}/verified-domains`, data),
  // Checks a workspace's custom domain against live DNS and records the answer.
  // Omit tenantId for your own workspace; pass one to check a workspace you
  // administer — a subsidiary, or any of them as a platform operator.
  verifyTenantCustomDomain: (tenantId) =>
    api.post('/api/branding/custom-domain/verify', null, {
      params: tenantId ? { tenant_id: tenantId } : {},
    }),
  listTenantExportApprovals: (tenantId) => api.get(`/api/control/tenants/${tenantId}/export-approvals`),
  createTenantExportApproval: (tenantId, data) => api.post(`/api/control/tenants/${tenantId}/export-approvals`, data),
  getTenantConfig: (tenantId, configKey) => api.get(`/api/control/tenants/${tenantId}/config/${configKey}`),
  updateTenantConfig: (tenantId, configKey, data) =>
    api.put(`/api/control/tenants/${tenantId}/config/${configKey}`, data),
  listPlugins: () => api.get('/api/control/plugins'),
  createPlugin: (data) => api.post('/api/control/plugins', data),
  toggleTenantPlugin: (tenantId, pluginKey, data) =>
    api.put(`/api/control/tenants/${tenantId}/plugins/${pluginKey}`, data),
  bootstrapPlatformCore: () => api.post('/api/control/platform-core/bootstrap'),
  getPlatformCoreDashboard: () => api.get('/api/control/platform-core/dashboard'),
  listTenantProvisioningJobs: (params) => api.get('/api/control/platform-core/provisioning/jobs', { params }),
  createTenantProvisioningJob: (data) => api.post('/api/control/platform-core/provisioning/jobs', data),
  processTenantProvisioningJob: (jobId, data) => api.post(`/api/control/platform-core/provisioning/jobs/${jobId}/process`, data),
  listPlatformServices: () => api.get('/api/control/platform-core/services'),
  listPlatformRoutes: () => api.get('/api/control/platform-core/routes'),
  listPlatformNotifications: (params) => api.get('/api/control/platform-core/notifications', { params }),
  getPlatformGovernanceProfile: () => api.get('/api/control/platform-core/governance'),
  updatePlatformGovernanceProfile: (data) => api.put('/api/control/platform-core/governance', data),
  listPlatformNotificationProviders: () => api.get('/api/control/platform-core/notifications/providers'),
  savePlatformNotificationProvider: (data) => api.put('/api/control/platform-core/notifications/providers', data),
  listPlatformNotificationTemplates: () => api.get('/api/control/platform-core/notifications/templates'),
  savePlatformNotificationTemplate: (data) => api.put('/api/control/platform-core/notifications/templates', data),
  queuePlatformNotification: (data) => api.post('/api/control/platform-core/notifications', data),
  processPlatformNotificationQueue: (data) => api.post('/api/control/platform-core/notifications/process', data),
  listPlatformActivity: (params) => api.get('/api/control/platform-core/activity', { params }),
  createPlatformActivity: (data) => api.post('/api/control/platform-core/activity', data),
  listPlatformStorageAssets: (params) => api.get('/api/control/platform-core/storage/assets', { params }),
  getBackupSummary: () => api.get('/api/control/backups/summary'),
  listBackupRuns: (params) => api.get('/api/control/backups/runs', { params }),
  runBackup: (data) => api.post('/api/control/backups/run', data, { timeout: 300000 }),
  verifyBackupRun: (runId) => api.post(`/api/control/backups/runs/${runId}/verify`),
  getTenantOperatingModel: (tenantId) => api.get(`/api/control/tenants/${tenantId}/operating-model`),
  updateTenantOperatingModel: (tenantId, data) => api.put(`/api/control/tenants/${tenantId}/operating-model`, data),
  getWorkspaceOperatingModel: () => api.get('/workspace/operating-model'),
  updateWorkspaceOperatingModel: (data) => api.put('/workspace/operating-model', data),
};

export const aiCommandAPI = {
  bootstrap: () => api.post('/api/ai/bootstrap'),
  getSuperadminCommandCenter: () => api.get('/api/ai/superadmin/command-center'),
  querySuperadmin: (query) => api.post('/api/ai/superadmin/query', { query }),
  getTenantCommandCenter: () => api.get('/api/ai/tenant/command-center'),
  queryCopilot: (query) => api.post('/api/ai/copilot/query', { query }),
  getArchitecture: () => api.get('/api/ai/architecture'),
};

// HR operations — requisitions, roster swaps, overtime, encashment, absence
// escalation, loans, succession, BGV, policy acknowledgements, safety,
// announcements. One backend router (/api/hr-ops), one client surface.
export const hrOpsAPI = {
  listRequisitions: (params) => api.get('/api/hr-ops/requisitions', { params }),
  createRequisition: (data) => api.post('/api/hr-ops/requisitions', data),
  decideRequisition: (id, data) => api.post(`/api/hr-ops/requisitions/${id}/decision`, data),

  listRosterSwaps: (params) => api.get('/api/hr-ops/roster-swaps', { params }),
  createRosterSwap: (data) => api.post('/api/hr-ops/roster-swaps', data),
  acceptRosterSwap: (id) => api.post(`/api/hr-ops/roster-swaps/${id}/accept`),
  decideRosterSwap: (id, data) => api.post(`/api/hr-ops/roster-swaps/${id}/decision`, data),

  listOvertime: (params) => api.get('/api/hr-ops/overtime', { params }),
  claimOvertime: (data) => api.post('/api/hr-ops/overtime', data),
  decideOvertime: (id, data) => api.post(`/api/hr-ops/overtime/${id}/decision`, data),

  listEncashments: (params) => api.get('/api/hr-ops/leave-encashments', { params }),
  createEncashment: (data) => api.post('/api/hr-ops/leave-encashments', data),
  decideEncashment: (id, data) => api.post(`/api/hr-ops/leave-encashments/${id}/decision`, data),

  listAbsenceEscalations: (params) => api.get('/api/hr-ops/absence-escalations', { params }),
  scanAbsences: (thresholdDays) => api.post('/api/hr-ops/absence-escalations/scan', { threshold_days: thresholdDays }),
  resolveAbsence: (id, data) => api.post(`/api/hr-ops/absence-escalations/${id}/resolve`, data),

  listLoans: (params) => api.get('/api/hr-ops/loans', { params }),
  createLoan: (data) => api.post('/api/hr-ops/loans', data),
  decideLoan: (id, data) => api.post(`/api/hr-ops/loans/${id}/decision`, data),
  recordRepayment: (id, data) => api.post(`/api/hr-ops/loans/${id}/repayments`, data),

  listSuccessionPlans: () => api.get('/api/hr-ops/succession-plans'),
  createSuccessionPlan: (data) => api.post('/api/hr-ops/succession-plans', data),
  addSuccessionCandidate: (planId, data) => api.post(`/api/hr-ops/succession-plans/${planId}/candidates`, data),
  archiveSuccessionPlan: (planId) => api.delete(`/api/hr-ops/succession-plans/${planId}`),

  listBgv: (params) => api.get('/api/hr-ops/background-verifications', { params }),
  createBgv: (data) => api.post('/api/hr-ops/background-verifications', data),
  updateBgv: (id, data) => api.put(`/api/hr-ops/background-verifications/${id}`, data),

  assignPolicyAcks: (data) => api.post('/api/hr-ops/policy-acknowledgements/assign', data),
  listPolicyAcks: (params) => api.get('/api/hr-ops/policy-acknowledgements', { params }),
  acknowledgePolicy: (id) => api.post(`/api/hr-ops/policy-acknowledgements/${id}/acknowledge`),

  listSafetyRecords: (params) => api.get('/api/hr-ops/safety-records', { params }),
  createSafetyRecord: (data) => api.post('/api/hr-ops/safety-records', data),
  updateSafetyRecord: (id, data) => api.put(`/api/hr-ops/safety-records/${id}`, data),

  listAnnouncements: (params) => api.get('/api/hr-ops/announcements', { params }),
  createAnnouncement: (data) => api.post('/api/hr-ops/announcements', data),
  updateAnnouncement: (id, data) => api.put(`/api/hr-ops/announcements/${id}`, data),
  archiveAnnouncement: (id) => api.delete(`/api/hr-ops/announcements/${id}`),
};

// Reimbursement supporting documents. Upload goes through the validated
// storage path, so this posts multipart rather than a URL.
export const reimbursementAttachmentAPI = {
  list: (claimId) => api.get(`/api/payroll/reimbursements/${claimId}/attachments`),
  upload: (claimId, file) => {
    const form = new FormData();
    form.append('file', file);
    return api.post(`/api/payroll/reimbursements/${claimId}/attachments`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

// ---------------------------------------------------------------------------
// Employee self-service surfaces
// ---------------------------------------------------------------------------

export const learningAPI = {
  listCourses: (params) => api.get('/api/learning/courses', { params }),
  createCourse: (data) => api.post('/api/learning/courses', data),
  listEnrollments: (params) => api.get('/api/learning/enrollments', { params }),
  upsertEnrollment: (data) => api.post('/api/learning/enrollments', data),
};

export const skillsAPI = {
  listCatalog: () => api.get('/api/skills/catalog'),
  createSkill: (data) => api.post('/api/skills/catalog', data),
  listRecords: (params) => api.get('/api/skills/records', { params }),
  upsertRecord: (data) => api.post('/api/skills/records', data),
};

export const okrAPI = {
  list: (params) => api.get('/api/performance/okrs', { params }),
  create: (data) => api.post('/api/performance/okrs', data),
};

export const referralAPI = {
  list: (params) => api.get('/api/recruitment/referrals', { params }),
  create: (data) => api.post('/api/recruitment/referrals', data),
  // Live openings only — the admin /jobs listing includes drafts and stays gated.
  listOpenJobs: () => api.get('/api/recruitment/jobs/open'),
};

export const itStatementAPI = {
  get: (params) => api.get('/api/payroll/it-statement', { params }),
};

export const sessionAPI = {
  listMine: () => api.get('/api/auth/sessions'),
  revoke: (sessionId) => api.post(`/api/auth/sessions/${sessionId}/revoke`),
  // Admin surface — everything below is workspace_admin / hr_admin only and
  // stays inside the caller's tenant server-side.
  searchUsers: (params) => api.get('/api/admin/session-users', { params }),
  listForUser: (userId) => api.get('/api/admin/sessions', { params: { user_id: userId } }),
  revokeAny: (sessionId) => api.post(`/api/admin/sessions/${sessionId}/revoke`),
  listRules: () => api.get('/api/admin/session-rules'),
  upsertRule: (data) => api.put('/api/admin/session-rules', data),
  deleteRule: (ruleId) => api.delete(`/api/admin/session-rules/${ruleId}`),
};

// Account locks and the auto punch-out log. Lives beside sessionAPI because it
// is the same audience and the same screen — an account lock is a security
// action, not an attendance one, even though the trigger arrives from a punch.
export const accountLockAPI = {
  list: (params) => api.get('/api/admin/account-locks', { params }),
  // The note is required by the API, not just the column: an unlock nobody can
  // explain in six months is the one the next auditor asks about.
  unlock: (userId, note) => api.post(`/api/admin/account-locks/${userId}/unlock`, { note }),
};

// What the system closed, why, and off what evidence. An enforced policy nobody
// can inspect is a support queue.
export const autoCloseAPI = {
  list: (params) => api.get('/api/admin/attendance/auto-closed', { params }),
  beacons: (attendanceId) => api.get(`/api/admin/attendance/${attendanceId}/beacons`),
  // dry_run defaults true server-side; pass false only to actually close.
  runSweep: (dryRun = true) =>
    api.post('/api/admin/attendance/auto-close/run', {}, { params: { dry_run: dryRun } }),
};

// Product help & FAQs. The server picks the articles from the tenant's vertical
// and the caller's role, so there is nothing to pass and nothing to filter here.
// Distinct from knowledgeBaseAPI, which is the tenant's own written content.
export const helpAPI = {
  listArticles: (params) => api.get('/api/help/articles', { params }),
  getArticle: (slug) => api.get(`/api/help/articles/${slug}`),
  listCategories: () => api.get('/api/help/categories'),
  markHelpful: (slug) => api.post(`/api/help/articles/${slug}/helpful`, {}),
};

// Superadmin-only — routes_event_bus gates every call on is_superadmin.
export const eventBusAPI = {
  listDomainEvents: (limit = 50) => api.get('/api/events/domain', { params: { limit } }),
  listSubscriptions: () => api.get('/api/events/subscriptions'),
  listDeliveries: (limit = 50) => api.get('/api/events/deliveries', { params: { limit } }),
  processQueue: (limit = 50) => api.post('/api/events/process', { limit }),
};

// Advisory & read-only by design — the authoritative gate stays server-side in
// plan_access, and plan CRUD stays under workspaceAdminAPI /subscription/*.
export const planCatalogAPI = {
  listPlans: () => api.get('/api/plan-catalog/plans'),
  mine: () => api.get('/api/plan-catalog/me'),
  listFeatures: () => api.get('/api/plan-catalog/features'),
  featureAccess: (key) => api.get(`/api/plan-catalog/features/${key}`),
  listAddOns: () => api.get('/api/plan-catalog/add-ons'),
  billingEstimate: () => api.get('/api/plan-catalog/billing-estimate'),
  upgradeEstimate: (targetTier) =>
    api.get('/api/plan-catalog/upgrade-estimate', { params: { target_tier: targetTier } }),
};

export const directoryAPI = {
  list: (params) => api.get('/api/directory', { params }),
  teammates: () => api.get('/api/directory/teammates'),
  orgChart: (params) => api.get('/api/directory/org-chart', { params }),
  detail: (employeeId) => api.get(`/api/directory/${employeeId}`),
};

export const knowledgeBaseAPI = {
  list: (params) => api.get('/api/helpdesk/kb', { params }),
  categories: () => api.get('/api/helpdesk/kb/categories'),
  get: (slug) => api.get(`/api/helpdesk/kb/${slug}`),
  markHelpful: (slug) => api.post(`/api/helpdesk/kb/${slug}/helpful`),
  create: (data) => api.post('/api/helpdesk/kb', data),
  update: (slug, data) => api.put(`/api/helpdesk/kb/${slug}`, data),
  remove: (slug) => api.delete(`/api/helpdesk/kb/${slug}`),
};

export const notificationPreferenceAPI = {
  get: () => api.get('/api/employees/me/notification-preferences'),
  update: (data) => api.put('/api/employees/me/notification-preferences', data),
};

// The .ics feed is a file download, not JSON — callers hand the blob to the
// browser rather than rendering it.
export const holidayCalendarAPI = {
  downloadIcs: (year) =>
    api.get('/api/holidays/calendar.ics', {
      params: year ? { year } : undefined,
      responseType: 'blob',
    }),
};

// ---------------------------------------------------------------------------
// Transactional email (superadmin-owned)
// ---------------------------------------------------------------------------

export const emailAdminAPI = {
  listIdentities: () => api.get('/api/control/platform-core/email-identities'),
  getIdentity: (tenantId) => api.get(`/api/control/platform-core/email-identities/${tenantId}`),
  saveIdentity: (tenantId, data) =>
    api.put(`/api/control/platform-core/email-identities/${tenantId}`, data),
  // Sends one real email and reports the SMTP outcome verbatim.
  testIdentity: (tenantId, to) =>
    api.post(`/api/control/platform-core/email-identities/${tenantId}/test`, null, {
      params: to ? { to } : undefined,
    }),

  listTemplates: (tenantId) =>
    api.get('/api/control/platform-core/notification-templates', {
      params: tenantId ? { tenant_id: tenantId } : undefined,
    }),
  saveTemplate: (templateKey, data, tenantId) =>
    api.put(`/api/control/platform-core/notification-templates/${templateKey}`, data, {
      params: tenantId ? { tenant_id: tenantId } : undefined,
    }),
  revertTemplate: (templateKey, tenantId) =>
    api.delete(`/api/control/platform-core/notification-templates/${templateKey}`, {
      params: { tenant_id: tenantId },
    }),
  previewTemplate: (templateKey, data) =>
    api.post(`/api/control/platform-core/notification-templates/${templateKey}/preview`, data),

  queue: (params) => api.get('/api/control/platform-core/notifications/queue', { params }),
  drain: (limit = 50) =>
    api.post('/api/control/platform-core/notifications/drain', null, { params: { limit } }),
};

// What a tenant admin may see about mail sent in their name: read-only.
export const emailTenantAPI = {
  identity: () => api.get('/api/email/identity'),
  templates: () => api.get('/api/email/templates'),
};
